import { createApi } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createBackendBaseQuery, transformApiError } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';
import { TokenManager } from '../../utils/tokenManager';
import {
  getMsalInstanceSafely,
  getAccountsSafely,
  waitForMsalInitialization,
  isMsalReady,
} from '../../utils/msalSetup';

export interface RolePermission {
  [key: string]: boolean;
}

export interface RoleNavigation {
  menuName: string;
  description: string;
  url: string;
  icon: string;
  sequence: number;
}

export interface LoggedInUserModule {
  moduleId: number;
  moduleName: string;
  regionIds: number[];
  roles: string[];
}

export interface CurrentUserPermission {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  modules: LoggedInUserModule[];
  /** Union of roles across all of the user's modules. */
  roles: string[];
  /** Union of region ids across all of the user's modules. */
  regionIds: number[];
  rolePermissions: RolePermission;
  roleNavigations: RoleNavigation[];
}

const BASE_URL = process.env.REACT_APP_USER_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_USER_API_BASE_URL environment variable is not set');
}

// Create the base query using the reusable factory
const backendBaseQuery = createBackendBaseQuery(BASE_URL);

/**
 * Base query that guarantees the user's country (fetched from Entra ID /
 * Microsoft Graph on first access) is available and sent as the `country`
 * header.
 *
 * The endpoint's `query()` is evaluated by RTK Query before the request is
 * issued, so reading the country there races with the Graph fetch and yields a
 * blank header on the very first call. Resolving the country here — after MSAL
 * is ready and inside the async base query — ensures the header is always
 * populated.
 */
const baseQueryWithCountry: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // Ensure the country is fetched the first time before building the request
  if (!TokenManager.hasCountry()) {
    try {
      if (!isMsalReady()) {
        await waitForMsalInitialization();
      }
      const instance = getMsalInstanceSafely();
      const account = getAccountsSafely()[0];
      if (instance && account) {
        // fetchUserCountry needs a Graph (frontend) token; make sure one exists
        if (!TokenManager.getFrontendToken()) {
          await TokenManager.refreshFrontendToken(instance, account);
        }
        await TokenManager.fetchUserCountry(instance, account);
      }
    } catch (error) {
      // Non-fatal: proceed without country rather than blocking the request
      console.warn('[currentUserPermissionApi] Failed to resolve user country:', error);
    }
  }

  const country = TokenManager.getCountry();
  const argsWithCountry: FetchArgs =
    typeof args === 'string'
      ? { url: args, headers: { country } }
      : { ...args, headers: { ...(args.headers as Record<string, string>), country } };

  return backendBaseQuery(argsWithCountry, api, extraOptions);
};

export const currentUserPermissionApiSlice = createApi({
  reducerPath: 'currentUserPermissionApi',
  baseQuery: baseQueryWithCountry,
  endpoints(builders) {
    return {
      fetchCurrentUserPermissions: builders.query<CurrentUserPermission, void>({
        query() {
          return API_URLS.USER_MANAGEMENT.LOGGED_IN_USER;
        },
        // The API returns role/region data per module (modules[].roles / modules[].regionIds).
        // Derive flat roles/regionIds (union across all modules) for existing consumers that
        // expect a single list, while still exposing the per-module data via `modules`.
        transformResponse: (response: CurrentUserPermission): CurrentUserPermission => {
          const modules = Array.isArray(response?.modules) ? response.modules : [];
          const roles = Array.from(new Set(modules.flatMap((m) => m.roles ?? [])));
          const regionIds = Array.from(new Set(modules.flatMap((m) => m.regionIds ?? [])));
          return { ...response, modules, roles, regionIds };
        },
        transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const { useFetchCurrentUserPermissionsQuery } = currentUserPermissionApiSlice;
